import React, { useEffect, useState } from 'react';
import AvailableMonthsPicker, { ALL_MONTHS } from './AvailableMonthsPicker';
import AdminModal from './AdminModal';
import { formatMonthRanges } from '../../../utils/bookingAvailability';

const TAB_GROUP = 'group';
const TAB_PRIVATE = 'private';
const TAB_BOTH = 'both';

function previewLabel(months) {
	if (!months?.length) return 'Closed — not bookable';
	if (months.length === 12) return 'All year';
	return formatMonthRanges(months);
}

function initMonths(stored, mode) {
	if (mode === 'closed') return [];
	if (mode === 'all_year' || !mode) return [...ALL_MONTHS];
	if (Array.isArray(stored) && stored.length) return [...stored];
	return [...ALL_MONTHS];
}

export default function JourneyAvailabilityModal({
	open,
	title,
	subtitle,
	groupEnabled = true,
	privateEnabled = true,
	initialMonths = [],
	initialGroupMonths = [],
	initialPrivateMonths = [],
	groupMode = 'all_year',
	privateMode = 'all_year',
	initialChannel = null,
	legacyMode = false,
	saving,
	onCancel,
	onSave,
}) {
	const [channel, setChannel] = useState(TAB_BOTH);
	const legacyInitial = initMonths(initialMonths, initialMonths?.length === 0 ? 'all_year' : 'seasonal');
	const [groupMonths, setGroupMonths] = useState(legacyMode ? legacyInitial : initialGroupMonths);
	const [privateMonths, setPrivateMonths] = useState(legacyMode ? legacyInitial : initialPrivateMonths);

	useEffect(() => {
		if (!open) return;
		if (legacyMode) {
			const months = initMonths(initialMonths, initialMonths?.length === 0 ? 'all_year' : 'seasonal');
			setGroupMonths(months);
			setPrivateMonths(months);
			return;
		}
		setGroupMonths(initMonths(initialGroupMonths, groupMode));
		setPrivateMonths(initMonths(initialPrivateMonths, privateMode));
		if (initialChannel === TAB_GROUP) setChannel(TAB_GROUP);
		else if (initialChannel === TAB_PRIVATE) setChannel(TAB_PRIVATE);
		else if (groupEnabled && privateEnabled) setChannel(TAB_BOTH);
		else if (groupEnabled) setChannel(TAB_GROUP);
		else setChannel(TAB_PRIVATE);
	// eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when modal opens (keyed remount in parent)
	}, [open]);

	if (!open) return null;

	if (legacyMode) {
		const months = groupMonths;
		return (
			<AdminModal open={open} onClose={onCancel} className="admin-cms-modal--availability" ariaLabelledBy="journey-availability-title">
				<div className="admin-cms-modal__head">
					<div>
						<h2 id="journey-availability-title">{title}</h2>
						{subtitle && <p className="admin-cms-muted mb-0">{subtitle}</p>}
					</div>
					<button type="button" className="close" onClick={onCancel} aria-label="Close">
						<span aria-hidden="true">&times;</span>
					</button>
				</div>
				<div className="admin-cms-modal__body">
					<p className="admin-cms-availability-preview">
						Customers will see: <strong>{previewLabel(months)}</strong>
					</p>
					<AvailableMonthsPicker value={months} onChange={setGroupMonths} />
				</div>
				<div className="admin-cms-modal__foot">
					<button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
						Cancel
					</button>
					<button type="button" className="btn btn-primary" onClick={() => onSave(months.length === 12 ? [] : months)} disabled={saving}>
						{saving ? 'Saving…' : 'Save availability'}
					</button>
				</div>
			</AdminModal>
		);
	}

	const showBothTab = groupEnabled && privateEnabled;
	const showGroup = channel === TAB_GROUP || channel === TAB_BOTH;
	const showPrivate = channel === TAB_PRIVATE || channel === TAB_BOTH;

	const handleSave = () => {
		if (channel === TAB_BOTH) {
			onSave({ channel: 'both', groupMonths, privateMonths, months: groupMonths });
		} else if (channel === TAB_GROUP) {
			onSave({ channel: 'group', groupMonths, months: groupMonths });
		} else {
			onSave({ channel: 'private', privateMonths, months: privateMonths });
		}
	};

	return (
		<AdminModal open={open} onClose={onCancel} className="admin-cms-modal--availability" ariaLabelledBy="journey-availability-title">
			<div className="admin-cms-modal__head">
				<div>
					<h2 id="journey-availability-title">{title}</h2>
					{subtitle && <p className="admin-cms-muted mb-0">{subtitle}</p>}
				</div>
				<button type="button" className="close" onClick={onCancel} aria-label="Close">
					<span aria-hidden="true">&times;</span>
				</button>
			</div>
			<div className="admin-cms-modal__body">
				{showBothTab && (
					<ul className="nav nav-pills admin-cms-availability-tabs mb-3">
						<li className="nav-item">
							<button
								type="button"
								className={`nav-link${channel === TAB_BOTH ? ' active' : ''}`}
								onClick={() => setChannel(TAB_BOTH)}
							>
								Group + Private (same)
							</button>
						</li>
						<li className="nav-item">
							<button
								type="button"
								className={`nav-link${channel === TAB_GROUP ? ' active' : ''}`}
								onClick={() => setChannel(TAB_GROUP)}
							>
								Group only
							</button>
						</li>
						<li className="nav-item">
							<button
								type="button"
								className={`nav-link${channel === TAB_PRIVATE ? ' active' : ''}`}
								onClick={() => setChannel(TAB_PRIVATE)}
							>
								Private only
							</button>
						</li>
					</ul>
				)}

				{!groupEnabled && privateEnabled && (
					<p className="admin-cms-muted small mb-2">This journey is private-tour only.</p>
				)}
				{groupEnabled && !privateEnabled && (
					<p className="admin-cms-muted small mb-2">This journey is group-departure only.</p>
				)}

				{showGroup && (
					<div className={showBothTab && channel === TAB_BOTH ? 'mb-4' : ''}>
						{showBothTab && channel === TAB_BOTH && (
							<p className="admin-cms-availability-preview">
								Group + private customers will see: <strong>{previewLabel(groupMonths)}</strong>
							</p>
						)}
						{channel === TAB_GROUP && (
							<p className="admin-cms-availability-preview">
								Group departures: <strong>{previewLabel(groupMonths)}</strong>
							</p>
						)}
						{(channel === TAB_BOTH || channel === TAB_GROUP) && (
							<AvailableMonthsPicker
								value={groupMonths}
								onChange={(months) => {
									setGroupMonths(months);
									if (channel === TAB_BOTH) setPrivateMonths(months);
								}}
							/>
						)}
					</div>
				)}

				{showPrivate && channel === TAB_PRIVATE && (
					<div>
						<p className="admin-cms-availability-preview">
							Private tours: <strong>{previewLabel(privateMonths)}</strong>
						</p>
						<AvailableMonthsPicker value={privateMonths} onChange={setPrivateMonths} />
					</div>
				)}
			</div>
			<div className="admin-cms-modal__foot">
				<button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
					Cancel
				</button>
				<button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
					{saving ? 'Saving…' : 'Save availability'}
				</button>
			</div>
		</AdminModal>
	);
}
